import { ReactNode } from "react";
import { StandardError } from "@/lib/error/error-utils";
import { cn } from "@/lib/utils";
import { QueryErrorDisplay } from "./QueryErrorDisplay";

interface EmptyStateWithErrorProps {
  title: string;
  description: string;
  error?: StandardError | null;
  onRetry?: () => void;
  onHome?: () => void;
  actionButton?: ReactNode;
  className?: string;
  size?: "sm" | "md" | "lg";
  icon?: ReactNode;
}

export function EmptyStateWithError({
  title,
  description,
  error,
  onRetry,
  onHome,
  actionButton,
  className,
  size = "md",
  icon,
}: EmptyStateWithErrorProps) {
  // If there's an error, show error state instead
  if (error) {
    return (
      <QueryErrorDisplay
        error={error}
        onRetry={onRetry}
        onHome={onHome}
        className={className}
        size={size}
      />
    );
  }

  const containerPadding = {
    sm: "py-6",
    md: "py-12",
    lg: "py-16",
  };

  return (
    <div className={cn("text-center", containerPadding[size], className)}>
      {icon && <div className="mb-4 flex justify-center">{icon}</div>}

      <h3
        className={cn(
          "font-semibold mb-2 text-foreground",
          size === "sm" ? "text-base" : "text-lg"
        )}
      >
        {title}
      </h3>

      <p className={cn("text-muted-foreground mb-4", size === "sm" ? "text-sm" : "text-base")}>
        {description}
      </p>

      {actionButton && <div className="flex justify-center">{actionButton}</div>}
    </div>
  );
}

interface LoadingStateProps {
  message?: string;
  className?: string;
  size?: "sm" | "md" | "lg";
}

export function LoadingState({
  message = "Loading...",
  className,
  size = "md",
}: LoadingStateProps) {
  const containerPadding = {
    sm: "py-4",
    md: "py-8",
    lg: "py-12",
  };

  const spinnerSize = {
    sm: "h-6 w-6",
    md: "h-8 w-8",
    lg: "h-12 w-12",
  };

  return (
    <div className={cn("text-center", containerPadding[size], className)}>
      <div className="flex justify-center mb-4">
        <div
          className={cn(
            "animate-spin rounded-full border-2 border-muted border-t-foreground",
            spinnerSize[size]
          )}
        />
      </div>
      <p className={cn("text-muted-foreground", size === "sm" ? "text-sm" : "text-base")}>
        {message}
      </p>
    </div>
  );
}
